import { C } from '../../../../lib/design';
import { Ic, ICON_PATHS } from '../../../../lib/ui';
import { useI18n } from '../../../../i18n';
import { DOC_COLORS } from '../../../../config/documentStyles';
import { Section, EmptyState, InlineEdit, InlineSelect, AddItemRow } from './ProcessSheetHelpers';
import type { EntradaItem, SalidaItem, FichaEditHook } from './ProcessSheetHelpers';

interface ProcessSheetInputsOutputsProps {
  entradas: FichaEditHook<EntradaItem>;
  salidas: FichaEditHook<SalidaItem>;
  procesosList: { id: string; codigo: string; nombre: string }[];
  editMode: boolean;
  addingSection: string | null;
  setAddingSection: (v: string | null) => void;
  newItemValue: string;
  setNewItemValue: (v: string) => void;
  onAdd: (
    section: string,
    hook: { crear: (d: Record<string, unknown>) => Promise<unknown> },
  ) => void;
}

export function ProcessSheetInputsOutputs({
  entradas,
  salidas,
  procesosList,
  editMode,
  addingSection,
  setAddingSection,
  newItemValue,
  setNewItemValue,
  onAdd,
}: ProcessSheetInputsOutputsProps) {
  const { t } = useI18n();

  return (
    <Section
      icon={<Ic d={ICON_PATHS.arrowRight} s={16} />}
      title={`3. ${t.processSheet.sections.inputsOutputs}`}
      fullWidth
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: DOC_COLORS.h3,
              textTransform: 'uppercase',
              marginBottom: 8,
            }}
          >
            {t.processSheet.fields.inputs}
          </div>
          {entradas.items.length === 0 && !entradas.loading ? (
            <EmptyState message={t.processSheet.noData} />
          ) : (
            entradas.items.map((item) => (
              <div
                key={item.id}
                style={{
                  padding: '6px 0',
                  borderBottom: `1px solid ${C.borderAlt}`,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <InlineEdit
                    value={item.descripcion}
                    onSave={(v) => entradas.actualizar(item.id, { descripcion: v })}
                    editMode={editMode}
                  />
                  {editMode && (
                    <button
                      onClick={() => entradas.eliminar(item.id)}
                      style={{
                        border: 'none',
                        background: 'none',
                        color: '#EF4444',
                        cursor: 'pointer',
                        fontSize: 14,
                        padding: 2,
                      }}
                    >
                      x
                    </button>
                  )}
                </div>
                {editMode && (
                  <div style={{ marginTop: 4 }}>
                    <InlineSelect
                      value={item.procesoOrigenId ?? ''}
                      options={procesosList.map((p) => ({
                        value: p.id,
                        label: `${p.codigo} ${p.nombre}`,
                      }))}
                      onSave={(v) => entradas.actualizar(item.id, { procesoOrigenId: v || null })}
                      editMode={editMode}
                      style={{ fontSize: 11 }}
                    />
                  </div>
                )}
              </div>
            ))
          )}
          {editMode && (
            <AddItemRow
              sectionKey="entradas"
              addingSection={addingSection}
              setAddingSection={setAddingSection}
              newItemValue={newItemValue}
              setNewItemValue={setNewItemValue}
              onAdd={() => onAdd('entradas', entradas)}
              addLabel={t.processSheet.addItem}
            />
          )}
        </div>
        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: '#16A34A',
              textTransform: 'uppercase',
              marginBottom: 8,
            }}
          >
            {t.processSheet.fields.outputs}
          </div>
          {salidas.items.length === 0 && !salidas.loading ? (
            <EmptyState message={t.processSheet.noData} />
          ) : (
            salidas.items.map((item) => (
              <div
                key={item.id}
                style={{
                  padding: '6px 0',
                  borderBottom: `1px solid ${C.borderAlt}`,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <InlineEdit
                    value={item.descripcion}
                    onSave={(v) => salidas.actualizar(item.id, { descripcion: v })}
                    editMode={editMode}
                  />
                  {editMode && (
                    <button
                      onClick={() => salidas.eliminar(item.id)}
                      style={{
                        border: 'none',
                        background: 'none',
                        color: '#EF4444',
                        cursor: 'pointer',
                        fontSize: 14,
                        padding: 2,
                      }}
                    >
                      x
                    </button>
                  )}
                </div>
                {editMode && (
                  <div style={{ marginTop: 4 }}>
                    <InlineSelect
                      value={item.procesoDestinoId ?? ''}
                      options={procesosList.map((p) => ({
                        value: p.id,
                        label: `${p.codigo} ${p.nombre}`,
                      }))}
                      onSave={(v) => salidas.actualizar(item.id, { procesoDestinoId: v || null })}
                      editMode={editMode}
                      style={{ fontSize: 11 }}
                    />
                  </div>
                )}
              </div>
            ))
          )}
          {editMode && (
            <AddItemRow
              sectionKey="salidas"
              addingSection={addingSection}
              setAddingSection={setAddingSection}
              newItemValue={newItemValue}
              setNewItemValue={setNewItemValue}
              onAdd={() => onAdd('salidas', salidas)}
              addLabel={t.processSheet.addItem}
            />
          )}
        </div>
      </div>
    </Section>
  );
}
